/**
 * /admin/plaza-settings — Agency Backend Settings for HustlePlaza
 *
 * Edits the live `plaza_settings` Mongo doc that drives the marketplace's
 * conversion mechanics: founding credit value, referral bonus, founding
 * cap, scarcity counter, suburb leaderboard, cart-abandon recovery.
 *
 * NOTE on auth: this page mounts directly at /admin/plaza-settings. When
 * the production admin auth wrapper is wired up, drop this route inside
 * the existing admin <ProtectedRoute> like the other /admin/* pages.
 */
import { useEffect, useState } from "react";
import {
  ChevronLeft, Save, ToggleLeft, ToggleRight, Loader2, CheckCircle2,
  DollarSign, Users, Flame, MapPin, Mail, Tag,
} from "lucide-react";
import "./PlazaSettingsAdminPage.css";
import PlazaCategoriesManager from "@/components/plaza/PlazaCategoriesManager";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
export default function PlazaSettingsAdminPage() {
  const [state, setState]   = useState(null);
  const [loading, setLoad]  = useState(true);
  const [saving, setSav]    = useState(false);
  const [savedAt, setSaved] = useState(null);
  const [err, setErr]       = useState("");
  const [stats, setStats]   = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/directory/admin/plaza/settings`).then((r) => r.json()),
      fetch(`${API}/api/directory/plaza/stats`).then((r) => r.json()),
    ]).then(([s, t]) => {
      setState(s);
      setStats(t);
      setLoad(false);
    }).catch(() => {
      setErr("Could not load settings — backend unavailable.");
      setLoad(false);
    });
  }, []);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSav(true);
    setErr("");
    try {
      const res = await fetch(`${API}/api/directory/admin/plaza/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(state),
      });
      if (!res.ok) throw new Error("save failed");
      const fresh = await res.json();
      setState(fresh);
      setSaved(new Date());
    } catch (e) {
      setErr("Could not save — try again in a moment.");
    } finally {
      setSav(false);
    }
  };

  if (loading) {
    return (
      <div className="psa-root psa-root--loading">
        <Loader2 size={28} className="psa-spin" /> Loading settings…
      </div>
    );
  }
  if (!state) {
    return (
      <div className="psa-root psa-root--loading">
        <p style={{ color: "#FF9F87" }}>{err || "No data."}</p>
      </div>
    );
  }

  const remaining = stats ? Math.max(0, state.founding_cap - stats.total_members) : null;

  return (
    <div className="psa-root" data-testid="plaza-settings-root">
      {/* Topbar */}
      <header className="psa-top">
        <a href="/agency/settings" className="psa-back" data-testid="plaza-settings-back">
          <ChevronLeft size={16} /> Suite Console
        </a>
        <h1>Agency Backend · Plaza Settings</h1>
        <button
          className="psa-save"
          onClick={save}
          disabled={saving}
          data-testid="plaza-settings-save"
        >
          {saving ? <><Loader2 size={14} className="psa-spin" /> Saving…</>
                  : <><Save size={14} /> Save changes</>}
        </button>
      </header>

      {/* Live snapshot */}
      {stats && (
        <section className="psa-snap" data-testid="plaza-settings-snap">
          <div className="psa-snap__cell">
            <span className="psa-snap__label">Total members</span>
            <strong>{stats.total_members.toLocaleString("en-AU")}</strong>
          </div>
          <div className="psa-snap__cell">
            <span className="psa-snap__label">This week</span>
            <strong>+{stats.members_this_week.toLocaleString("en-AU")}</strong>
          </div>
          <div className="psa-snap__cell">
            <span className="psa-snap__label">Founding credits remaining</span>
            <strong>{remaining?.toLocaleString("en-AU")}</strong>
          </div>
          <div className="psa-snap__cell">
            <span className="psa-snap__label">Top suburb</span>
            <strong>{stats.top_suburbs[0]?.suburb || "—"}</strong>
          </div>
        </section>
      )}

      {err && <div className="psa-err" data-testid="plaza-settings-err">{err}</div>}
      {savedAt && !saving && (
        <div className="psa-ok" data-testid="plaza-settings-ok">
          <CheckCircle2 size={14} /> Saved at {savedAt.toLocaleTimeString("en-AU")}
        </div>
      )}

      {/* Editable groups */}
      <Group title="Shop Categories (show/hide & order)" icon={Tag}>
        <PlazaCategoriesManager />
      </Group>

      <Group title="Credits & Rewards" icon={DollarSign}>
        <Field
          label="Signup credit (AUD)"
          help="Awarded to every member when they join the waitlist."
          type="number"
          value={state.signup_credit_amount}
          onChange={(v) => set("signup_credit_amount", parseInt(v || "0", 10))}
          testid="set-signup-credit"
        />
        <Field
          label="Referral bonus credit (AUD)"
          help="Awarded once a member's referrals hit the threshold."
          type="number"
          value={state.referral_credit_amount}
          onChange={(v) => set("referral_credit_amount", parseInt(v || "0", 10))}
          testid="set-referral-credit"
        />
        <Field
          label="Referral threshold"
          help="How many confirmed referrals before the referrer gets the bonus."
          type="number"
          value={state.referral_threshold}
          onChange={(v) => set("referral_threshold", parseInt(v || "0", 10))}
          testid="set-referral-threshold"
        />
        <Field
          label="Credit currency"
          help='Display label for credits. "AUD" or "PTS".'
          type="text"
          value={state.credit_currency}
          onChange={(v) => set("credit_currency", v)}
          testid="set-currency"
        />
      </Group>

      <Group title="Founding-Member Cap" icon={Flame}>
        <Field
          label="Founding-member cap"
          help="Total number of $25 credits to give out. Burns down with each signup; drives the scarcity counter."
          type="number"
          value={state.founding_cap}
          onChange={(v) => set("founding_cap", parseInt(v || "0", 10))}
          testid="set-founding-cap"
        />
        <Field
          label="Launch label (display only)"
          help='e.g. "June 2026" — shown on the marketplace.'
          type="text"
          value={state.launch_label}
          onChange={(v) => set("launch_label", v)}
          testid="set-launch-label"
        />
      </Group>

      <Group title="Feature Flags" icon={ToggleRight}>
        <Toggle
          label="Show scarcity counter"
          help="Shows '612 of 5,000 credits remaining' card on the marketplace."
          value={state.scarcity_counter_enabled}
          onChange={(v) => set("scarcity_counter_enabled", v)}
          testid="set-flag-scarcity"
        />
        <Toggle
          label="Show suburb leaderboard"
          help="Shows top SE QLD suburbs joining now, builds local social proof."
          value={state.suburb_leaderboard_enabled}
          onChange={(v) => set("suburb_leaderboard_enabled", v)}
          testid="set-flag-suburbs"
        />
        <Toggle
          label="Enable cart-abandon save"
          help="When checked, the cart drawer's 'Save my basket' opens an email-capture modal."
          value={state.cart_abandon_email_enabled}
          onChange={(v) => set("cart_abandon_email_enabled", v)}
          testid="set-flag-cart-save"
        />
      </Group>

      <Group title="Suburb Leaderboard Tuning" icon={MapPin}>
        <Field
          label="Minimum members per suburb"
          help="Hide suburbs with fewer than N members. Use 1 to show all."
          type="number"
          value={state.suburb_min_members}
          onChange={(v) => set("suburb_min_members", parseInt(v || "1", 10))}
          testid="set-suburb-min"
        />
      </Group>

      <p className="psa-foot">
        Settings persist to <code>plaza_settings</code> in MongoDB. Changes
        apply to the marketplace within ~30 seconds (the public page refreshes
        stats every 30s).
      </p>
    </div>
  );
}

/* ─── Reusable bits ──────────────────────────────────────────── */
function Group({ title, icon: Icon, children }) {
  return (
    <section className="psa-group">
      <header className="psa-group__head">
        <div className="psa-group__icon"><Icon size={18} /></div>
        <h2>{title}</h2>
      </header>
      <div className="psa-group__body">{children}</div>
    </section>
  );
}

function Field({ label, help, value, onChange, type = "text", testid }) {
  return (
    <label className="psa-field">
      <span className="psa-field__label">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
      {help && <span className="psa-field__help">{help}</span>}
    </label>
  );
}

function Toggle({ label, help, value, onChange, testid }) {
  return (
    <div className="psa-toggle">
      <button
        className={`psa-toggle__btn ${value ? "on" : ""}`}
        onClick={() => onChange(!value)}
        type="button"
        data-testid={testid}
      >
        {value ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
      </button>
      <div>
        <p className="psa-toggle__label">{label}</p>
        {help && <p className="psa-toggle__help">{help}</p>}
      </div>
    </div>
  );
}
